import DatePicker from "./DatePicker";
import TimePicker from "./TimePicker";

type DateTimePickerProps = {
  label?: string;
  seconds?: number | null;
  onChange?: (seconds: number) => void;
  mandatory?: boolean;
  disableBeforeToday?: boolean;
  disabledSlots?: string[];
};

/**
 * Combined Date + Time picker.
 * Stores a single Unix timestamp (seconds) combining the chosen date and time.
 */
const DateTimePicker = ({
  label = "Date et heure",
  seconds = null,
  onChange = () => {},
  mandatory = false,
  disableBeforeToday = false,
  disabledSlots,
}: DateTimePickerProps) => {
  // Extract date-only seconds (midnight) and time string from combined timestamp
  const dateSeconds = seconds ? getMidnightSeconds(seconds) : null;
  const timeValue = seconds ? formatTimeFromSeconds(seconds) : null;

  const handleDateChange = (newDateSeconds: number) => {
    // Combine new date with existing time (or default 08:00)
    const time = timeValue || "08:00";
    const combined = combineDateAndTime(newDateSeconds, time);
    onChange(combined);
  };

  const handleTimeChange = (newTime: string) => {
    if (!newTime) return;
    // Combine existing date (or today) with new time
    const date = dateSeconds || getMidnightSeconds(Math.floor(Date.now() / 1000));
    const combined = combineDateAndTime(date, newTime);
    onChange(combined);
  };

  return (
    <div className="flex flex-col gap-1">
      {label && <div className="text-h4 text-neutral-400">{label}</div>}
      <div className="flex flex-row gap-2">
        <div className="flex-1">
          <DatePicker
            label=""
            seconds={dateSeconds}
            onChange={handleDateChange}
            mandatory={mandatory}
            disableBeforeToday={disableBeforeToday}
          />
        </div>
        <div className="w-120">
          <TimePicker
            label=""
            value={timeValue}
            onChange={handleTimeChange}
            minHour={7}
            maxHour={20}
            minuteStep={7.5}
            disabledSlots={disabledSlots}
          />
        </div>
      </div>
    </div>
  );
};

/** Get midnight (00:00) seconds for a given timestamp */
function getMidnightSeconds(seconds: number): number {
  const d = new Date(seconds * 1000);
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

/** Extract "HH:mm" from a Unix timestamp */
function formatTimeFromSeconds(seconds: number): string {
  const d = new Date(seconds * 1000);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Combine a midnight timestamp with a "HH:mm" string */
function combineDateAndTime(midnightSeconds: number, time: string): number {
  const [h, m] = time.split(":").map(Number);
  return midnightSeconds + h * 3600 + m * 60;
}

export default DateTimePicker;
